import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import DivKitCard from './DivKitCard';

interface Message {
  id: string;
  role: string;
  message_text: string;
  timestamp: Date;
  message_type?: string;
  payload?: any;
  action_type?: string;
  command_type?: string;
  control_type?: string;
  sequence_id?: string;
  full_content?: any;
}

type JSONValue = string | number | boolean | null | JSONObject | JSONArray;

interface JSONObject {
  [key: string]: JSONValue;
}

interface JSONArray extends Array<JSONValue> {}
type SessionData = JSONObject;

interface AppStatus {
  health: boolean;
  ready: boolean;
}

const API_BASE_URL = (window as any).runtimeConfig?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const WEBHOOK_BASE_URL = (window as any).runtimeConfig?.VITE_WEBHOOK_BASE_URL || import.meta.env.VITE_WEBHOOK_BASE_URL || 'http://localhost:3001';
const CUSTOM_AGENT_BRIDGE_URL = (window as any).runtimeConfig?.VITE_CUSTOM_AGENT_BRIDGE_URL || import.meta.env.VITE_CUSTOM_AGENT_BRIDGE_URL || WEBHOOK_BASE_URL;

// The memory service has no ingress: nginx in this container proxies /memory/ to it
const MEMORY_BASE_URL = '/memory';
const MEMORY_DATA_KINDS = ['public', 'private'] as const;

type MemoryServiceInfo = Partial<Record<(typeof MEMORY_DATA_KINDS)[number], JSONObject>>;

const isJsonObject = (value: unknown): value is JSONObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Same shape as self-play's memory_service_info: {"public": {...}, "private": {...}}
const parseMemoryServiceInfo = (text: string): MemoryServiceInfo | null => {
  if (!text.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(text);
    if (!isJsonObject(parsed)) return null;
    const info: MemoryServiceInfo = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (!(MEMORY_DATA_KINDS as readonly string[]).includes(key) || !isJsonObject(value)) return null;
      info[key as keyof MemoryServiceInfo] = value;
    }
    return info;
  } catch {
    return null;
  }
};

const pushMemory = async (conversationId: string, info: MemoryServiceInfo) => {
  for (const kind of MEMORY_DATA_KINDS) {
    const data = info[kind];
    if (data && Object.keys(data).length > 0) {
      await axios.post(`${MEMORY_BASE_URL}/v1/conversations/${conversationId}/${kind}-data`, data);
    }
  }
};

// Configure axios interceptor to add custom agent bridge URL header if set
if (CUSTOM_AGENT_BRIDGE_URL) {
  axios.interceptors.request.use((config) => {
    config.headers['X-Custom-Agent-Bridge-Url'] = CUSTOM_AGENT_BRIDGE_URL;
    return config;
  });
}

const App: React.FC = () => {
  const [status, setStatus] = useState<AppStatus>({ health: false, ready: false });
  const [sessionId, setSessionId] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [rawMessages, setRawMessages] = useState<any[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [messageLanguage, setMessageLanguage] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventPayloadText, setEventPayloadText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'chat' | 'raw'>('chat');
  const [showOnlyTextMessages, setShowOnlyTextMessages] = useState(true);
  
  // Session creation form state
  const [sessionType, setSessionType] = useState<'v1' | 'v2' | 'v2-local'>('v2-local');
  const [autorun, setAutorun] = useState(false);
  const [sessionData, setSessionData] = useState<SessionData>({'user_name': 'Niamh', 'user_id': 'user_1'});
  const [language, setLanguage] = useState<string>('ru');
  const [startAgent, setStartAgent] = useState<string>('RAG_Agent_RU');
  const [autoFirstMessage, setAutoFirstMessage] = useState<string>('');
  const [agentName, setAgentName] = useState<string>('');
  const [agentsOverrides, setAgentsOverrides] = useState<{[key: string]: any}>({
    food_delivery: {
      fetch_memory_on_user_reply: true
    },
    clock_master: {
      fetch_memory_on_user_reply: true
    },
    weather: {
      fetch_memory_on_user_reply: true
    },
    hub: {
      fetch_memory_on_user_reply: true
    },
    aviata: {
      fetch_memory_on_user_reply: true,
      scopes: ['aviata']
    },
    ticketon: {
      scopes: ['ticketon']
    }
  });
  const [agentsOverridesRawText, setAgentsOverridesRawText] = useState<string | null>(null);
  
  // V1 specific state
  const [scenarioData, setScenarioData] = useState<any>(null);
  const [scenarioLoaded, setScenarioLoaded] = useState<boolean>(false);
  
  // V2 scenario specific state
  const [scenarioDataV2, setScenarioDataV2] = useState<any>(null);
  const [scenarioLoadedV2, setScenarioLoadedV2] = useState<boolean>(false);
  const [useExistingScenarioV2, setUseExistingScenarioV2] = useState<boolean>(false);
  const [existingScenarioIdV2, setExistingScenarioIdV2] = useState<string>('');
  
  // Auto-creation state
  const [shouldAutoCreate, setShouldAutoCreate] = useState<boolean>(false);
  const [isDirectScenarioLink, setisDirectScenarioLink] = useState<boolean>(false);
  
  // Conversation data state (for pre-loading before session creation)
  const [conversationData, setConversationData] = useState<any[]>([]);
  const [conversationLoaded, setConversationLoaded] = useState<boolean>(false);
  
  // Tools metadata state
  const [toolsMetadata, setToolsMetadata] = useState<any[]>([
    {
      name: 'rag_tool',
      tools_metadata: {
        RAG_Agent_RU: {
          mcp_tool_name: 'tool_rag_support_lola_direct',
          mcp_tool_version: 'latest',
          collection_id: '',
          output_document_id: false,
          output_document_score: false,
          document_score_threshold: 0.5,
          rag_service_unavailable_message: 'Document storage is not accessible right now',
          no_documents_found_message: 'No relevant documents found'
        },
        RAG_Agent_UZ: {
          mcp_tool_name: 'tool_rag_support_lola_direct',
          mcp_tool_version: 'latest',
          collection_id: '',
          output_document_id: false,
          output_document_score: false,
          document_score_threshold: 0.5,
          rag_service_unavailable_message: 'Document storage is not accessible right now',
          no_documents_found_message: 'No relevant documents found'
        }
      }
    },
    {
      name: 'call_human_operator_tool',
      tools_metadata: {
        RAG_Agent_RU: {
          emulate_operator_switch: true,
          send_switch_message: false,
          switch_messages: [
            "Пожалуйста, опишите вашу проблему. Оператор скоро присоединится.",
            "Я подключаю вас к оператору. Пожалуйста, опишите вашу проблему, пока он присоединяется.",
          ]
        },
        RAG_Agent_UZ: {
          emulate_operator_switch: true,
          send_switch_message: false,
          switch_messages: [
            "Iltimos, muammoingizni tasvirlab bering. Operator tez orada qo'shiladi.",
            "Sizni operatorga bog'layapman. U qo'shilayotgan paytda muammoingizni tasvirlab bering."
          ]
        }
      }
    },
    {
      name: 'request_contact_tool',
      tools_metadata: {
        Sales_Agent_RU: {
          button_text: "Отправить номер",
          text: "Поделитесь номером телефона"
        },
        Sales_Agent_UZ: {
          button_text: "Raqamni yuborish",
          text: "Iltimos, telefon raqamingizni ulashing"
        }
      }
    }
  ]);

  // Raw text state for metadata fields to allow typing without immediate validation
  const [metadataRawText, setMetadataRawText] = useState<{[key: string]: string}>({});
  
  // Raw text state for sessionData to allow typing without immediate validation
  const [sessionDataRawText, setSessionDataRawText] = useState<string>('');
  const [memoryServiceInfoText, setMemoryServiceInfoText] = useState<string>('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check app status on component mount and periodically
  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Focus input on component mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Focus input when session becomes active
  useEffect(() => {
    if (sessionId) {
      inputRef.current?.focus();
    }
  }, [sessionId]);

  // Focus input when loading completes
  useEffect(() => {
    if (!loading && sessionId) {
      inputRef.current?.focus();
    }
  }, [loading, sessionId]);

  // Auto-create session from URL parameter - step 1: detect URL param and set state
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const scenarioIdParam = urlParams.get('scenario_id');

    if (scenarioIdParam && !sessionId && !loading) {
      // Set session type to v2 and configure for existing scenario
      setSessionType('v2');
      setUseExistingScenarioV2(true);
      setExistingScenarioIdV2(scenarioIdParam);
      // autostart=false leaves the form open so memory and session data can be filled first
      if (urlParams.get('autostart') !== 'false') {
        setShouldAutoCreate(true);
        setisDirectScenarioLink(true);
      }
    }
  }, []); // Run only once on mount

  // Auto-create session from URL parameter - step 2: create session when state is ready
  useEffect(() => {
    if (shouldAutoCreate && sessionType === 'v2' && useExistingScenarioV2 && existingScenarioIdV2 && !sessionId && !loading) {
      setShouldAutoCreate(false); // Prevent multiple calls
      createSession();
    }
  }, [shouldAutoCreate, sessionType, useExistingScenarioV2, existingScenarioIdV2, sessionId, loading]);

  const checkStatus = async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/v1/health`),
        axios.get(`${API_BASE_URL}/api/v1/ready`)
      ]);
      setStatus({
        health:
          results[0].status === 'fulfilled' && (results[0] as PromiseFulfilledResult<any>).value.status === 200,
        ready:
          results[1].status === 'fulfilled' && (results[1] as PromiseFulfilledResult<any>).value.status === 200,
      });
    } catch (error) {
      console.error('Error checking status:', error);
      setStatus({ health: false, ready: false });
    }
  };

  // Helper function to clean up rich text editor artifacts
  const unescapeRichTextArtifacts = (text: string): string => {
    if (typeof text !== 'string') return text;
    
    // Replace double-escaped newlines with single newlines
    text = text.replace(/\\\n/g, '\n');
    
    // Replace escaped brackets
    text = text.replace(/\\\[/g, '[');
    text = text.replace(/\\\]/g, ']');
    
    // Replace double backslashes with single backslashes
    text = text.replace(/\\\\/g, '\\');
    
    return text;
  };

  const loadScenarioFile = async (file: File) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate required fields
      if (!data.id || !data.settings || !data.engineMetadata) {
        throw new Error('Invalid scenario file format');
      }
      
      // Clean up rich text artifacts from scenario parts
      if (data.engineMetadata?.prompt) {
        const prompt = data.engineMetadata.prompt;
        if (prompt.role) prompt.role = unescapeRichTextArtifacts(prompt.role);
        if (prompt.goal) prompt.goal = unescapeRichTextArtifacts(prompt.goal);
        if (prompt.example) prompt.example = unescapeRichTextArtifacts(prompt.example);
        if (prompt.flow) prompt.flow = unescapeRichTextArtifacts(prompt.flow);
        if (prompt.success) prompt.success = unescapeRichTextArtifacts(prompt.success);
        if (prompt.prompt_reminder) prompt.prompt_reminder = unescapeRichTextArtifacts(prompt.prompt_reminder);
      }
      
      setScenarioData(data);
      setScenarioLoaded(true);
      setError('');
      console.log('Loaded scenario:', data.name || data.id);
    } catch (error: any) {
      setError(`Failed to load scenario file: ${error.message}`);
      setScenarioLoaded(false);
    }
  };

  const handleScenarioFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      loadScenarioFile(file);
    }
  };

  const loadScenarioFileV2 = async (file: File) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate required fields for V2 genTools format
      if (!data.id || !data.engineMetadata?.genTools) {
        throw new Error('Invalid V2 scenario file format. Missing genTools structure.');
      }
      
      const genTools = data.engineMetadata.genTools;
      if (!genTools.settings || !genTools.agents || genTools.agents.length === 0) {
        throw new Error('Invalid V2 scenario file format. Missing settings or agents.');
      }
      
      setScenarioDataV2(data);
      setScenarioLoadedV2(true);
      setError('');
      console.log('Loaded V2 scenario:', data.name || data.id);
    } catch (error: any) {
      setError(`Failed to load V2 scenario file: ${error.message}`);
      setScenarioLoadedV2(false);
    }
  };

  const handleScenarioFileUploadV2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      loadScenarioFileV2(file);
    }
  };

  const loadConversationFile = async (file: File) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate the file is an array
      if (!Array.isArray(data)) {
        throw new Error('Invalid conversation file format. Expected array of messages: [{"role": "...", "content": "..."}, ...]');
      }
      
      setConversationData(data);
      setConversationLoaded(true);
      setError('');
      console.log('Loaded conversation with', data.length, 'messages');
    } catch (error: any) {
      setError(`Failed to load conversation file: ${error.message}`);
      setConversationLoaded(false);
    }
  };

  const handleConversationFileUploadPreSession = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      loadConversationFile(file);
    }
  };

  const createSession = async () => {
    try {
      setLoading(true);
      setError('');
      
      const newSessionId = uuidv4();

      const memoryServiceInfo = parseMemoryServiceInfo(memoryServiceInfoText);
      if (!memoryServiceInfo) {
        setError('Memory must be a JSON object like {"public": {...}, "private": {...}}');
        setLoading(false);
        return;
      }
      await pushMemory(newSessionId, memoryServiceInfo);

      if (sessionType === 'v1') {
        // Validate v1 requirements
        if (!scenarioLoaded || !scenarioData) {
          setError('Please load a scenario file first');
          setLoading(false);
          return;
        }
        
        // Step 1: Create/update scenario
        const scenarioPayload = {
          scenario_id: scenarioData.id,
          agent_type: scenarioData.settings.agentType,
          language: scenarioData.settings.language,
          scenario_parts: {
            role: scenarioData.engineMetadata.prompt.role || '',
            goal: scenarioData.engineMetadata.prompt.goal || '',
            example: scenarioData.engineMetadata.prompt.example || '',
            flow: scenarioData.engineMetadata.prompt.flow || '',
            success: scenarioData.engineMetadata.prompt.success || '',
            prompt_reminder: scenarioData.engineMetadata.prompt.prompt_reminder || null,
          },
          information_sources: scenarioData.engineMetadata.prompt.informationSources || null,
          default_session_data: scenarioData.engineMetadata.prompt.defaultSessionData || null,
        };
        
        await axios.post(`${API_BASE_URL}/api/v1/scenarios`, scenarioPayload);
        
        // Step 2: Create session with scenario_id
        const payloadV1: any = {
          version: "v1",
          scenario_id: scenarioData.id,
          autorun: autorun,
          language: scenarioData.settings.language,
          channel_type: "text",
        };

        // Add session_data if provided (will merge with scenario defaults)
        if (Object.keys(sessionData).length > 0) {
          payloadV1.session_data = sessionData;
        }
        
        const requestBodyV1: any = {
          session_id: newSessionId,
          payload: payloadV1,
          agent_name: agentName || null,
          conversation_token: uuidv4(),
        };
        
        await axios.post(`${API_BASE_URL}/api/v1/sessions`, requestBodyV1);
        
      } else if (sessionType === 'v2') {
        // V2 scenario-based session creation
        
        if (useExistingScenarioV2) {
          // Using existing scenario_id - skip scenario upload
          if (!existingScenarioIdV2.trim()) {
            setError('Please provide a scenario_id');
            setLoading(false);
            return;
          }
          
          // Create session with existing scenario_id directly
          const payloadV2: any = {
            version: "v2",
            scenario_id: existingScenarioIdV2,
            ...(Object.keys(getResolvedAgentsOverrides()).length > 0 && { agents_overrides: getResolvedAgentsOverrides() }),
          };

          // If this is a URL-based session, only send minimal payload
          // The scenario config already has all settings, so don't override them
          if (!isDirectScenarioLink) {
            // For manually created sessions, include the UI form values
            payloadV2.autorun = autorun;
            payloadV2.language = language;

            // Add session_data if provided (will merge with scenario defaults)
            if (Object.keys(sessionData).length > 0) {
              payloadV2.session_data = sessionData;
            }
          }

          const requestBodyV2: any = {
            session_id: newSessionId,
            payload: payloadV2,
            conversation_token: uuidv4(),
            auto_first_message: autoFirstMessage || undefined,
          };

          await axios.post(`${API_BASE_URL}/api/v1/sessions`, requestBodyV2);
          
        } else {
          // Upload new scenario file
          // Validate v2 scenario requirements
          if (!scenarioLoadedV2 || !scenarioDataV2) {
            setError('Please load a V2 scenario file first');
            setLoading(false);
            return;
          }
          
          const genTools = scenarioDataV2.engineMetadata.genTools;
          const settings = genTools.settings;
          const agents = genTools.agents;
          const analytics = genTools.analytics || [];
          
          // Create mapping from agent ID to agent name
          const agentIdToName: { [key: string]: string } = {};
          agents.forEach((agent: any) => {
            const agentId = agent.id || '';
            const agentName = agent.agent_name || agentId;
            if (agentId) {
              agentIdToName[agentId] = agentName;
            }
          });
          
          // Convert start_agent from ID to name if needed
          let startAgentName = settings.start_agent || '';
          if (startAgentName && agentIdToName[startAgentName]) {
            startAgentName = agentIdToName[startAgentName];
          }
          
          // Extract prephrases - use PrephrasesConfig if latency/probability are available
          const prephrasesList = settings.prephrases || [];
          const prephrasesLatency = settings.prephrases_latency;
          const prephrasesProbability = settings.prephrases_probability;
          
          let prephrasesConfig: any;
          if (prephrasesLatency !== undefined || prephrasesProbability !== undefined) {
            // Create PrephrasesConfig object
            prephrasesConfig = {
              prephrases: prephrasesList,
              latency: prephrasesLatency !== undefined ? prephrasesLatency : 1000,
              probability: prephrasesProbability !== undefined ? prephrasesProbability : 0.9,
            };
          } else {
            // Use simple list if no latency/probability
            prephrasesConfig = prephrasesList;
          }
          
          // Step 1: Create/update scenario via V2 API
          const scenarioPayloadV2: any = {
            scenario_id: scenarioDataV2.id,
            autorun: settings.autorun !== undefined ? settings.autorun : autorun,
            language: settings.language || language,
            timezone: settings.timezone || null,
            agent_type: settings.agent_type || 'voice',
            stream_method: settings.stream_method || 'sentence_stream',
            start_agent: startAgentName,
            prephrases: prephrasesConfig,
            default_session_data: settings.default_session_data || null,
            default_tone_of_voice: settings.default_tone_of_voice || null,
            reactivation_delay_intervals: settings.reactivation_delay_intervals || [],
            tools_prephrases: settings.tools_prephrases || {},
            agents: agents.map((agent: any) => {
              const handoffAgents = agent.handoffs !== undefined
                ? agent.handoffs.map((handoff: any) => {
                  const target = agentIdToName[handoff.agent_id] || handoff.agent_id;
                  const mode = handoff.mode || 'handoff';
                  const hint = handoff.handoff_hint?.trim();
                  if (mode === 'handoff' && !hint) {
                    return target;
                  }
                  return {
                    to: target,
                    ...(mode === 'delegate' ? { mode } : {}),
                    ...(hint ? { hint } : {}),
                  };
                })
                : (agent.handoff_agents || []).map((handoff: string | { to: string }) => {
                  if (typeof handoff === 'string') {
                    return agentIdToName[handoff] || handoff;
                  }
                  return {
                    ...handoff,
                    to: agentIdToName[handoff.to] || handoff.to,
                  };
                });
              
              return {
                agent_name: agent.agent_name || agent.id || '',
                inference_preset: agent.inference_preset ?? null,
                agent_class: agent.agent_class ?? 'CoordinatedAgent',
                shared_among_all_agents: agent.shared_among_all_agents ?? true,
                prompt: agent.prompt || '',
                prompt_reminder: agent.prompt_reminder || null,
                prephrases: agent.prephrases ?? null,
                flow: agent.flow || '',
                goal: agent.goal || '',
                role: agent.role || '',
                success: agent.success || '',
                examples: agent.examples || '',
                lang_style: agent.lang_style || '',
                tools: agent.tools || [],
                handoff_agents: handoffAgents,
              };
            }),
            analytics: analytics.map((analyticsConfig: any) => ({
              prompt: analyticsConfig.prompt || '',
              examples: analyticsConfig.examples || '',
              targets: analyticsConfig.targets || [],
              triggers: analyticsConfig.triggers || [],
              agent_mode: analyticsConfig.agent_mode || 'classic',
              analytics_agent: analyticsConfig.analytics_agent || null,
            })),
            metadata: scenarioDataV2.version ? (() => {
              const metadataObj: { [key: string]: string } = {};
              for (const [key, value] of Object.entries(scenarioDataV2.version)) {
                metadataObj[key] = String(value);
              }
              return metadataObj;
            })() : null,
          };
          
          await axios.post(`${API_BASE_URL}/api/v2/scenarios`, scenarioPayloadV2);
          
          // Step 2: Create session with scenario_id via V1 endpoint (both endpoints use same function)
          const payloadV2: any = {
            version: "v2",
            scenario_id: scenarioDataV2.id,
            autorun: settings.autorun !== undefined ? settings.autorun : autorun,
            language: settings.language || language,
            start_agent: startAgentName,
            channel_type: "text",
            ...(Object.keys(getResolvedAgentsOverrides()).length > 0 && { agents_overrides: getResolvedAgentsOverrides() }),
          };

          // Add session_data if provided (will merge with scenario defaults)
          if (Object.keys(sessionData).length > 0) {
            payloadV2.session_data = sessionData;
          }

          const requestBodyV2: any = {
            session_id: newSessionId,
            payload: payloadV2,
            conversation_token: uuidv4(),
            auto_first_message: autoFirstMessage || undefined,
          };

          await axios.post(`${API_BASE_URL}/api/v1/sessions`, requestBodyV2);
        }
        
      } else {
        // V2-local session creation (local config-based)
        // Validate required fields
        if (!startAgent.trim()) {
          setError('Start agent is required');
          setLoading(false);
          return;
        }
        
        // Build payload for v2-local
        const payloadV2Local: any = {
          version: "v2",
          session_data: Object.keys(sessionData).length > 0 ? sessionData : undefined,
          tools_metadata: toolsMetadata.map(tool => ({
            tool_name: tool.name,
            tools_metadata: tool.tools_metadata
          })),
          language: language,
          autorun,
          start_agent: startAgent || undefined,
          channel_type: "text",
          ...(Object.keys(getResolvedAgentsOverrides()).length > 0 && { agents_overrides: getResolvedAgentsOverrides() }),
        };

        // Build request body for v2-local
        const requestBodyV2Local: any = {
          session_id: newSessionId,
          conversation_token: uuidv4(),
          payload: payloadV2Local,
          auto_first_message: autoFirstMessage || undefined,
        };

        await axios.post(`${API_BASE_URL}/api/v1/sessions`, requestBodyV2Local);
      }
      
      setSessionId(newSessionId);
      const sessionTypeLabel = sessionType === 'v2-local' ? 'v2-local' : sessionType;
      const initialMessages: Message[] = [{
        id: uuidv4(),
        role: 'system',
        message_text: `Session (${sessionTypeLabel}) created with ID: ${newSessionId}`,
        message_type: 'TextMessage',
        timestamp: new Date()
      }];

      // Register this session with our webhook server
      await axios.post(`${WEBHOOK_BASE_URL}/register-session`, {
        sessionId: newSessionId
      });

      // If conversation was pre-loaded, import it now
      if (conversationLoaded && conversationData.length > 0) {
        try {
          // Import messages to the session
          await axios.post(`${API_BASE_URL}/api/v1/sessions/${newSessionId}/messages`, {
            messages: conversationData
          });

          // Add imported messages to UI
          const importedMessages: Message[] = conversationData.map((msg: any) => ({
            id: uuidv4(),
            role: msg.role,
            message_text: msg.content,
            timestamp: new Date(),
            message_type: 'TextMessage',
          }));

          initialMessages.push({
            id: uuidv4(),
            role: 'system',
            message_text: `Imported ${conversationData.length} pre-loaded messages`,
            message_type: 'TextMessage',
            timestamp: new Date()
          });

          initialMessages.push(...importedMessages);

          // Clear the pre-loaded conversation
          setConversationData([]);
          setConversationLoaded(false);
        } catch (importError: any) {
          setError(`Session created but failed to import conversation: ${importError.response?.data?.detail || importError.message}`);
        }
      }

      setMessages(initialMessages);
      setRawMessages([]);

    } catch (error: any) {
      setError(`Failed to create session: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      setError('');
      
      await axios.delete(`${API_BASE_URL}/api/v1/sessions/${sessionId}`);
      
      // Unregister session from webhook server
      await axios.post(`${WEBHOOK_BASE_URL}/unregister-session`, {
        sessionId
      });

      // If this was a URL-based session, a reload must open the form instead of auto-starting again
      if (isDirectScenarioLink) {
        const url = new URL(window.location.href);
        url.searchParams.set('autostart', 'false');
        window.history.replaceState({}, '', url.toString());
        setisDirectScenarioLink(false);
      }

      setSessionId('');
      setMessages([]);
      setRawMessages([]);
    } catch (error: any) {
      setError(`Failed to delete session: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !sessionId) return;

    const userMessage: Message = {
      id: uuidv4(),
      role: 'user',
      message_text: inputMessage,
      timestamp: new Date(),
      message_type: 'TextMessage',
    };

  setMessages((prev: Message[]) => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');

    try {
      setLoading(true);
      setError('');

      await axios.post(`${API_BASE_URL}/api/v1/messages`, {
        session_id: sessionId,
        message_type: "TextMessage",
        message_text: messageToSend,
        ...(messageLanguage.trim() && { agent_language: messageLanguage.trim() })
      });

    } catch (error: any) {
      setError(`Failed to send message: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
      // Focus input after sending message - use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  };

  const sendSilence = async () => {
    if (!sessionId) return;

    const silenceMessage: Message = {
      id: uuidv4(),
      role: 'user',
      message_text: messageLanguage.trim() ? `Silence (${messageLanguage.trim()})` : 'Silence',
      timestamp: new Date(),
      message_type: 'SilenceMessage',
    };

    setMessages((prev: Message[]) => [...prev, silenceMessage]);

    try {
      setLoading(true);
      setError('');

      await axios.post(`${API_BASE_URL}/api/v1/messages`, {
        session_id: sessionId,
        message_type: "SilenceMessage",
        ...(messageLanguage.trim() && { agent_language: messageLanguage.trim() })
      });

    } catch (error: any) {
      setError(`Failed to send silence: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
      // Focus input after sending silence - use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  };

  const handleSend = async () => {
    if (!sessionId) return;

    if (inputMessage.trim()) {
      await sendMessage();
    } else {
      await sendSilence();
    }
  };

  const isEventPayloadValid = () => {
    if (!eventPayloadText.trim()) return true;
    try {
      JSON.parse(eventPayloadText);
      return true;
    } catch {
      return false;
    }
  };

  const sendUserEvent = async () => {
    if (!eventType.trim() || !sessionId || !isEventPayloadValid()) return;

    const payload = eventPayloadText.trim() ? JSON.parse(eventPayloadText) : undefined;
    const eventMessage: Message = {
      id: uuidv4(),
      role: 'user',
      message_text: eventType.trim(),
      timestamp: new Date(),
      message_type: 'UserEvent',
      payload,
    };

    setMessages((prev: Message[]) => [...prev, eventMessage]);

    try {
      setLoading(true);
      setError('');

      await axios.post(`${API_BASE_URL}/api/v1/messages`, {
        session_id: sessionId,
        message_type: 'UserEvent',
        event_id: uuidv4(),
        event_type: eventType.trim(),
        ...(payload !== undefined && { payload }),
      });

    } catch (error: any) {
      setError(`Failed to send user event: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadConversationHistory = async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      setError('');

      const response = await axios.get(`${API_BASE_URL}/api/v1/sessions/${sessionId}/messages`);
      
      // Create a blob from the JSON data
      const jsonBlob = new Blob([JSON.stringify(response.data, null, 2)], { 
        type: 'application/json' 
      });
      
      // Create a download link and trigger it
      const url = URL.createObjectURL(jsonBlob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `conversation-${sessionId.slice(0, 8)}-${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error: any) {
      setError(`Failed to download conversation history: ${error.response?.data?.error?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const importConversationFromFile = async (file: File) => {
    if (!sessionId) {
      setError('Please create a session first before importing a conversation');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Read and parse the JSON file
      const fileText = await file.text();
      const conversationData = JSON.parse(fileText);

      // Validate the file is an array
      if (!Array.isArray(conversationData)) {
        throw new Error('Invalid file format. Expected array of messages: [{"role": "...", "content": "..."}, ...]');
      }

      // Import messages to the existing session
      await axios.post(`${API_BASE_URL}/api/v1/sessions/${sessionId}/messages`, {
        messages: conversationData
      });

      // Load messages into UI
      const importedMessages: Message[] = conversationData.map((msg: any) => ({
        id: uuidv4(),
        role: msg.role,
        message_text: msg.content,
        timestamp: new Date(),
        message_type: 'TextMessage',
      }));

      setMessages((prev) => [
        ...prev,
        {
          id: uuidv4(),
          role: 'system',
          message_text: `Imported ${conversationData.length} messages into current session`,
          message_type: 'TextMessage',
          timestamp: new Date()
        },
        ...importedMessages
      ]);

    } catch (error: any) {
      setError(`Failed to import conversation: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConversationFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importConversationFromFile(file);
    }
  };

  // Listen for incoming messages and analytics events from webhook
  useEffect(() => {
    if (!sessionId) return;

    const eventSource = new EventSource(`${WEBHOOK_BASE_URL}/messages/${sessionId}`);
    
    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      // Handle analytics events - add to raw messages tab
      if (data.event_type === 'analytics') {
        console.log('Received analytics event:', data);
        
        const eventName = data.event_name || 'unknown';
        const payload = data.payload || {};
        
        // Extract relevant data based on event type
        let rawMessage: any = {
          event_name: eventName,
          timestamp: data.timestamp || new Date().toISOString(),
        };
        
        if (eventName === 'agent_user_message') {
          // Extract message_text and message_type
          rawMessage.role = 'user';
          rawMessage.message_type = payload.message_type;
          rawMessage.content = payload.message_text;
        } else if (eventName === 'agent_iteration_response') {
          // Extract raw_response and action type (tool, speak, finish, etc.)
          rawMessage.role = 'assistant';
          rawMessage.message_type = payload.action_type ?? 'unknown';
          rawMessage.content = payload.raw_response || '';
        } else if (eventName === 'llm_analytics_response') {
          // Extract LLM analytics response
          rawMessage.role = 'analytics';
          rawMessage.content = `${payload.llm_response || ''}`;
        } else if (eventName === 'memory_snapshot') {
          rawMessage.role = 'analytics';
          rawMessage.content = JSON.stringify(payload.memory_snapshot ?? {}, null, 2);
        } else {
          return; // Skip other analytics events
        }
        
        setRawMessages((prev) => [...prev, rawMessage]);
        return;
      }

      // Skip messages with empty message_text unless they're control/command messages
      if (!data.message_text && data.message_type === 'TextMessage') {
        console.log('Skipping message with empty text:', data);
        return;
      }

      // Handle regular messages
      // Skip user messages since they're already added locally when sent
      if (data.role === 'user') {
        console.log('Skipping user message:', data);
        return;
      }

      console.log('Received assistant message:', data);

      if (data.message_type === 'ActionMessage' && data.action_type === 'divkit') {
        setRawMessages((prev) => [...prev, {
          event_name: 'action_message',
          role: 'assistant',
          message_type: 'divkit',
          content: JSON.stringify(data.payload, null, 2),
          timestamp: data.timestamp || new Date().toISOString(),
        }]);
      }

      const assistantMessage: Message = {
        id: uuidv4(),
        role: data.role || 'assistant',
        message_text: data.message_text || '',
        timestamp: new Date(),
        message_type: data.message_type,
        payload: data.payload,
        action_type: data.action_type,
        command_type: data.command_type,
        control_type: data.control_type,
        sequence_id: data.sequence_id,
        full_content: JSON.stringify(data),
      };
      setMessages((prev: Message[]) => [...prev, assistantMessage]);
    };

    eventSource.onerror = (error) => {
      console.error('EventSource error:', error);
    };

    return () => {
      eventSource.close();
    };
  }, [sessionId]);


  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Tools metadata helper functions
  const addTool = () => {
    setToolsMetadata([...toolsMetadata, {
      name: 'new_tool',
      tools_metadata: {
        'New_Agent': {
          'example_field': 'example_value'
        }
      }
    }]);
  };

  const removeTool = (index: number) => {
    setToolsMetadata(toolsMetadata.filter((_, i) => i !== index));
  };

  const updateToolName = (index: number, name: string) => {
    const updated = [...toolsMetadata];
    updated[index].name = name;
    setToolsMetadata(updated);
  };

  const updateAgentName = (toolIndex: number, oldAgentName: string, newAgentName: string) => {
    const updated = [...toolsMetadata];
    const tool = updated[toolIndex];
    const agentData = tool.tools_metadata[oldAgentName];
    delete tool.tools_metadata[oldAgentName];
    tool.tools_metadata[newAgentName] = agentData;
    setToolsMetadata(updated);
  };

  const addAgentToTool = (toolIndex: number) => {
    const updated = [...toolsMetadata];
    const newAgentName = `Agent_${Object.keys(updated[toolIndex].tools_metadata).length + 1}`;
    updated[toolIndex].tools_metadata[newAgentName] = {
      'example_field': 'example_value'
    };
    setToolsMetadata(updated);
  };

  const removeAgentFromTool = (toolIndex: number, agentName: string) => {
    const updated = [...toolsMetadata];
    const tool = updated[toolIndex];
    if (Object.keys(tool.tools_metadata).length > 1) {
      delete tool.tools_metadata[agentName];
      setToolsMetadata(updated);
    }
  };

  // Helper functions for metadata text handling
  const getMetadataKey = (toolIndex: number, agentName: string) => `${toolIndex}-${agentName}`;

  const getMetadataText = (toolIndex: number, agentName: string, agentData: any) => {
    const key = getMetadataKey(toolIndex, agentName);
    return metadataRawText[key] !== undefined ? metadataRawText[key] : JSON.stringify(agentData, null, 2);
  };

  const updateMetadataText = (toolIndex: number, agentName: string, text: string) => {
    const key = getMetadataKey(toolIndex, agentName);
    setMetadataRawText(prev => ({ ...prev, [key]: text }));
  };

  const applyMetadataChanges = (toolIndex: number, agentName: string) => {
    const key = getMetadataKey(toolIndex, agentName);
    const text = metadataRawText[key];
    if (text !== undefined) {
      try {
        const parsed = JSON.parse(text);
        const updated = [...toolsMetadata];
        updated[toolIndex].tools_metadata[agentName] = parsed;
        setToolsMetadata(updated);
        // Clear the raw text since we've successfully applied it
        setMetadataRawText(prev => {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        });
      } catch (error) {
        // Invalid JSON, keep the raw text for editing
        console.log('Invalid JSON, keeping raw text for editing');
      }
    }
  };

  const isMetadataValid = (toolIndex: number, agentName: string) => {
    const key = getMetadataKey(toolIndex, agentName);
    const text = metadataRawText[key];
    if (text === undefined) return true; // No changes made
    try {
      JSON.parse(text);
      return true;
    } catch {
      return false;
    }
  };

  // Helper functions for sessionData text handling
  const getSessionDataText = () => {
    return sessionDataRawText !== '' ? sessionDataRawText : JSON.stringify(sessionData, null, 2);
  };

  const updateSessionDataText = (text: string) => {
    setSessionDataRawText(text);
  };

  const applySessionDataChanges = () => {
    if (sessionDataRawText !== '') {
      try {
        const parsed = JSON.parse(sessionDataRawText);
        setSessionData(parsed);
        // Clear the raw text since we've successfully applied it
        setSessionDataRawText('');
      } catch (error) {
        // Invalid JSON, keep the raw text for editing
        console.log('Invalid JSON, keeping raw text for editing');
      }
    }
  };

  const isSessionDataValid = () => {
    if (sessionDataRawText === '') return true; // No changes made
    try {
      JSON.parse(sessionDataRawText);
      return true;
    } catch {
      return false;
    }
  };

  // Helper functions for agentsOverrides text handling
  const getAgentsOverridesText = () => {
    return agentsOverridesRawText !== null ? agentsOverridesRawText : JSON.stringify(agentsOverrides, null, 2);
  };

  const updateAgentsOverridesText = (text: string) => {
    setAgentsOverridesRawText(text);
  };

  const applyAgentsOverridesChanges = () => {
    if (agentsOverridesRawText === null) return;
    if (agentsOverridesRawText.trim() === '') {
      setAgentsOverrides({});
      setAgentsOverridesRawText(null);
      return;
    }
    try {
      const parsed = JSON.parse(agentsOverridesRawText);
      setAgentsOverrides(parsed);
      setAgentsOverridesRawText(null);
    } catch {
      // Invalid JSON, keep the raw text for editing
    }
  };

  const getResolvedAgentsOverrides = (): {[key: string]: any} => {
    if (agentsOverridesRawText === null) return agentsOverrides;
    if (agentsOverridesRawText.trim() === '') return {};
    try {
      return JSON.parse(agentsOverridesRawText);
    } catch {
      return agentsOverrides;
    }
  };

  const isDivkitMessage = (message: Message) =>
    message.message_type === 'ActionMessage' && message.action_type === 'divkit';

  const renderMessageContent = (message: Message) => {
    const { message_type, message_text, payload, action_type, command_type, control_type, full_content } = message;

    switch (message_type) {
      case 'TextMessage':
        return message_text;

      case 'DataMessage':
        return (
          <div>
            <div className="payload-title data">Data message</div>
            <pre className="code">{JSON.stringify(payload, null, 2)}</pre>
          </div>
        );

      case 'ActionMessage':
        if (isDivkitMessage(message) && Array.isArray(payload?.data)) {
          return (
            <div>
              {payload.data.map((cardJson: any, index: number) => (
                <DivKitCard key={index} json={cardJson} id={`divkit-${message.id}-${index}`} />
              ))}
            </div>
          );
        }
        return (
          <div>
            <div className="payload-title action">Action: {action_type}</div>
            {payload && <pre className="code">{JSON.stringify(payload, null, 2)}</pre>}
          </div>
        );

      case 'UserEvent':
        return (
          <div>
            <div className="payload-title event">User event: {message_text}</div>
            {payload && <pre className="code">{JSON.stringify(payload, null, 2)}</pre>}
          </div>
        );

      case 'CommandMessage':
        return <div className="payload-title command">Command: {command_type}</div>;

      case 'ControlMessage':
        return (
          <div>
            <div className="payload-title control">Control: {control_type}</div>
            <div className="payload-note">
              {control_type === 'SequenceStart' && 'Starting new message sequence'}
              {control_type === 'SequenceFinish' && 'Finishing message sequence'}
            </div>
          </div>
        );

      default:
        return full_content;
    }
  };

  const sessionTypeOptions: { value: 'v1' | 'v2' | 'v2-local'; label: string }[] = [
    { value: 'v1', label: 'V1 (scenario based)' },
    { value: 'v2', label: 'V2 (scenario based)' },
    { value: 'v2-local', label: 'V2 (local config based)' },
  ];

  const visibleMessages = messages.filter(
    (message) =>
      !showOnlyTextMessages ||
      message.message_type === 'TextMessage' ||
      message.message_type === 'UserEvent' ||
      isDivkitMessage(message),
  );

  const renderSessionDataField = () => (
    <div className="field">
      <label className="label" htmlFor="session-data">Session data (JSON)</label>
      <textarea
        id="session-data"
        rows={6}
        className={`textarea mono${isSessionDataValid() ? '' : ' is-invalid'}`}
        value={getSessionDataText()}
        onChange={(e) => updateSessionDataText(e.target.value)}
        onBlur={applySessionDataChanges}
        placeholder='{"key":"value"}'
      />
      {isSessionDataValid()
        ? <div className="hint">Merged over the scenario defaults when the session starts.</div>
        : <div className="hint hint-crit">Invalid JSON. Fix the syntax to apply it.</div>}
    </div>
  );

  const renderAgentsOverridesField = () => (
    <div className="field">
      <label className="label" htmlFor="agents-overrides">Agents overrides (JSON)</label>
      <textarea
        id="agents-overrides"
        rows={6}
        className="textarea mono"
        value={getAgentsOverridesText()}
        onChange={(e) => updateAgentsOverridesText(e.target.value)}
        onBlur={applyAgentsOverridesChanges}
        placeholder='e.g. {"agent_name": {"inference_preset": "qwen3_5_35b_a3b_no_reasoning", "agent_class": "CustomAgent"}}'
      />
      <div className="hint">Per-agent settings such as agent_class, inference_preset, scopes or fetch_memory_on_user_reply.</div>
    </div>
  );

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <h1 className="brand-name">Voice Agent Test UI</h1>
          <span className="brand-target" title={API_BASE_URL}>{API_BASE_URL}</span>
        </div>
        <div className="topbar-status">
          <span className={`pill ${status.health ? 'pill-ok' : 'pill-crit'}`}>{status.health ? 'Healthy' : 'Unhealthy'}</span>
          <span className={`pill ${status.ready ? 'pill-ok' : 'pill-warn'}`}>{status.ready ? 'Ready' : 'Not ready'}</span>
          <span className={`pill ${sessionId ? 'pill-live' : 'pill-idle'}`}>{sessionId ? 'Session live' : 'No session'}</span>
        </div>
      </header>

      <div className="workspace">
        <aside className="rail">
          {error && (
            <div className="banner" role="alert">
              <span>{error}</span>
              <button className="banner-dismiss" onClick={() => setError('')} aria-label="Dismiss error">×</button>
            </div>
          )}

          <div className="rail-head">
            <h2 className="rail-title">Session</h2>
          </div>

          {sessionId ? (
            <div className="stack">
              <div className="field">
                <span className="label">Session ID</span>
                <div className="session-id">{sessionId}</div>
              </div>

              <details className="section" open>
                <summary>Import conversation</summary>
                <div className="section-body stack stack-sm">
                  <label className="file-pick">
                    Choose conversation JSON
                    <input
                      type="file"
                      accept=".json"
                      className="visually-hidden"
                      onChange={handleConversationFileUpload}
                      disabled={loading}
                    />
                  </label>
                  <div className="hint">Appends the messages of a saved conversation to this session.</div>
                </div>
              </details>

              <button className="btn btn-danger btn-block" onClick={deleteSession} disabled={loading}>
                {loading ? 'Deleting...' : 'Delete Session'}
              </button>
            </div>
          ) : (
            <div className="stack">
              <div className="field">
                <span className="label">Session type</span>
                <div className="segmented" role="radiogroup" aria-label="Session type">
                  {sessionTypeOptions.map((option) => (
                    <label key={option.value} className="segmented-option">
                      <input
                        type="radio"
                        name="sessionType"
                        className="visually-hidden"
                        value={option.value}
                        checked={sessionType === option.value}
                        onChange={(e) => setSessionType(e.target.value as 'v1' | 'v2' | 'v2-local')}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <label className="check">
                <input type="checkbox" checked={autorun} onChange={(e) => setAutorun(e.target.checked)} />
                Autorun
              </label>

              <div className="field">
                <label className="label" htmlFor="memory-json">Memory (JSON, optional)</label>
                <textarea
                  id="memory-json"
                  rows={3}
                  className={`textarea mono${parseMemoryServiceInfo(memoryServiceInfoText) ? '' : ' is-invalid'}`}
                  value={memoryServiceInfoText}
                  onChange={(e) => setMemoryServiceInfoText(e.target.value)}
                  placeholder='{"public": {"project": "iqor", "channel": "iqor_call_filtering_evgeny_test"}, "private": {}}'
                />
                <div className="hint">Written to the memory service before the session starts, same shape as self-play memory_service_info.</div>
              </div>

              {sessionType === 'v1' && (
                <>
                  <details className="section" open>
                    <summary>Scenario</summary>
                    <div className="section-body stack">
                      <div className="field">
                        <span className="label">Scenario file (JSON)</span>
                        <label className="file-pick">
                          Choose scenario JSON
                          <input
                            type="file"
                            accept=".json"
                            className="visually-hidden"
                            onChange={handleScenarioFileUpload}
                          />
                        </label>
                        {scenarioLoaded && scenarioData && (
                          <div className="note note-ok">
                            <strong>Loaded: {scenarioData.name || scenarioData.id}</strong>
                            <div className="note-row"><span className="note-key">Type</span><span>{scenarioData.settings?.agentType}</span></div>
                            <div className="note-row"><span className="note-key">Language</span><span>{scenarioData.settings?.language}</span></div>
                          </div>
                        )}
                      </div>
                      <div className="field">
                        <label className="label" htmlFor="agent-name">Agent name (optional)</label>
                        <input
                          id="agent-name"
                          type="text"
                          className="input"
                          value={agentName}
                          onChange={(e) => setAgentName(e.target.value)}
                          placeholder="Agent name (optional)"
                        />
                      </div>
                    </div>
                  </details>

                  <details className="section">
                    <summary>Session data<span className="summary-tag">optional</span></summary>
                    <div className="section-body">{renderSessionDataField()}</div>
                  </details>
                </>
              )}

              {sessionType === 'v2' && (
                <>
                  <details className="section" open>
                    <summary>Scenario</summary>
                    <div className="section-body stack">
                      <div className="choice-list">
                        <label className="choice">
                          <input
                            type="radio"
                            name="v2ScenarioSource"
                            checked={!useExistingScenarioV2}
                            onChange={() => setUseExistingScenarioV2(false)}
                          />
                          Upload new scenario file
                        </label>
                        <label className="choice">
                          <input
                            type="radio"
                            name="v2ScenarioSource"
                            checked={useExistingScenarioV2}
                            onChange={() => setUseExistingScenarioV2(true)}
                          />
                          Use existing scenario_id
                        </label>
                      </div>

                      {!useExistingScenarioV2 ? (
                        <div className="field">
                          <span className="label">Scenario file (JSON)</span>
                          <label className="file-pick">
                            Choose scenario JSON
                            <input
                              type="file"
                              accept=".json"
                              className="visually-hidden"
                              onChange={handleScenarioFileUploadV2}
                            />
                          </label>
                          {scenarioLoadedV2 && scenarioDataV2 && (
                            <div className="note note-ok">
                              <strong>Loaded: {scenarioDataV2.name || scenarioDataV2.id}</strong>
                              <div className="note-row"><span className="note-key">Type</span><span>{scenarioDataV2.engineMetadata?.genTools?.settings?.agent_type || 'N/A'}</span></div>
                              <div className="note-row"><span className="note-key">Language</span><span>{scenarioDataV2.engineMetadata?.genTools?.settings?.language || 'N/A'}</span></div>
                              <div className="note-row"><span className="note-key">Start agent</span><span>{scenarioDataV2.engineMetadata?.genTools?.settings?.start_agent || 'N/A'}</span></div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="field">
                          <label className="label" htmlFor="scenario-id">Scenario ID</label>
                          <input
                            id="scenario-id"
                            type="text"
                            className="input mono"
                            value={existingScenarioIdV2}
                            onChange={(e) => setExistingScenarioIdV2(e.target.value)}
                            placeholder="Enter existing scenario_id"
                          />
                          {existingScenarioIdV2 && (
                            <div className="note note-accent">Will use scenario <strong>{existingScenarioIdV2}</strong></div>
                          )}
                        </div>
                      )}
                    </div>
                  </details>

                  <details className="section">
                    <summary>Session data<span className="summary-tag">optional</span></summary>
                    <div className="section-body">{renderSessionDataField()}</div>
                  </details>

                  <details className="section" open>
                    <summary>Agents overrides<span className="summary-tag">optional</span></summary>
                    <div className="section-body">{renderAgentsOverridesField()}</div>
                  </details>
                </>
              )}

              {sessionType === 'v2-local' && (
                <>
                  <details className="section" open>
                    <summary>Local config</summary>
                    <div className="section-body stack">
                      <div className="field">
                        <label className="label" htmlFor="language">Language</label>
                        <input id="language" type="text" className="input" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="Language (ru)" />
                      </div>
                      <div className="field">
                        <label className="label" htmlFor="start-agent">Start agent</label>
                        <input id="start-agent" type="text" className="input" value={startAgent} onChange={(e) => setStartAgent(e.target.value)} placeholder="Start agent (required)" />
                      </div>
                      {renderSessionDataField()}
                      <div className="field">
                        <label className="label" htmlFor="auto-first-message">Auto first message</label>
                        <input id="auto-first-message" type="text" className="input" value={autoFirstMessage} onChange={(e) => setAutoFirstMessage(e.target.value)} placeholder="Auto first message (optional)" />
                      </div>
                      {renderAgentsOverridesField()}
                    </div>
                  </details>

                  <details className="section" open>
                    <summary>Tools metadata<span className="summary-tag">{toolsMetadata.length} tools</span></summary>
                    <div className="section-body stack stack-sm">
                      {toolsMetadata.map((tool, toolIndex) => (
                        <div key={toolIndex} className="tool-card">
                          <div className="tool-head">
                            <span className="label">Tool</span>
                            <input
                              type="text"
                              className="input mono"
                              value={tool.name}
                              onChange={(e) => updateToolName(toolIndex, e.target.value)}
                              placeholder="Tool name"
                            />
                            <button className="btn btn-ghost btn-icon" onClick={() => removeTool(toolIndex)} aria-label="Remove tool" title="Remove tool">×</button>
                          </div>

                          {Object.entries(tool.tools_metadata).map(([agentName, agentData], agentIndex) => (
                            <div key={agentIndex} className="agent-card">
                              <div className="agent-head">
                                <span className="label">Agent</span>
                                <input
                                  type="text"
                                  className="input mono"
                                  value={agentName}
                                  onChange={(e) => updateAgentName(toolIndex, agentName, e.target.value)}
                                  placeholder="Agent name"
                                />
                                <button
                                  className="btn btn-ghost btn-icon"
                                  onClick={() => removeAgentFromTool(toolIndex, agentName)}
                                  disabled={Object.keys(tool.tools_metadata).length <= 1}
                                  aria-label="Remove agent"
                                  title="Remove agent"
                                >
                                  ×
                                </button>
                              </div>
                              <textarea
                                rows={4}
                                className={`textarea mono${isMetadataValid(toolIndex, agentName) ? '' : ' is-invalid'}`}
                                value={getMetadataText(toolIndex, agentName, agentData)}
                                onChange={(e) => updateMetadataText(toolIndex, agentName, e.target.value)}
                                onBlur={() => applyMetadataChanges(toolIndex, agentName)}
                                placeholder='{"field": "value"}'
                              />
                              {!isMetadataValid(toolIndex, agentName) && (
                                <div className="hint hint-crit">Invalid JSON. Fix the syntax to apply it.</div>
                              )}
                            </div>
                          ))}
                          <button className="btn btn-secondary btn-sm btn-block" onClick={() => addAgentToTool(toolIndex)}>
                            + Add agent
                          </button>
                        </div>
                      ))}
                      <button className="btn btn-secondary btn-block" onClick={addTool}>+ Add tool</button>
                    </div>
                  </details>
                </>
              )}

              <details className="section">
                <summary>Pre-load conversation<span className="summary-tag">{conversationLoaded ? `${conversationData.length} messages` : 'optional'}</span></summary>
                <div className="section-body stack stack-sm">
                  <label className="file-pick">
                    Choose conversation JSON
                    <input
                      type="file"
                      accept=".json"
                      className="visually-hidden"
                      onChange={handleConversationFileUploadPreSession}
                    />
                  </label>
                  {conversationLoaded && (
                    <div className="note note-ok">
                      <strong>Loaded: {conversationData.length} messages</strong>
                      <div>Imported right after the session is created.</div>
                    </div>
                  )}
                </div>
              </details>

              <button className="btn btn-primary btn-block" onClick={createSession} disabled={loading}>
                {loading ? 'Creating...' : 'Create Session'}
              </button>
            </div>
          )}
        </aside>

        <section className="stage">
          {sessionId ? (
            <>
              <div className="stage-bar">
                <div className="tabs">
                  <button className="tab" data-active={activeTab === 'chat'} onClick={() => setActiveTab('chat')}>
                    Chat
                  </button>
                  <button className="tab" data-active={activeTab === 'raw'} onClick={() => setActiveTab('raw')}>
                    Raw Messages
                  </button>
                </div>
                <div className="stage-actions">
                  <button className="btn btn-secondary btn-sm" onClick={downloadConversationHistory} disabled={loading}>
                    {loading ? 'Downloading...' : 'Download history'}
                  </button>
                </div>
              </div>

              <div className="panel">
                {activeTab === 'chat' ? (
                  <>
                    <div className="panel-toolbar">
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={showOnlyTextMessages}
                          onChange={(e) => setShowOnlyTextMessages(e.target.checked)}
                        />
                        Show only text messages
                      </label>
                      <span className="count">{visibleMessages.length} / {messages.length}</span>
                    </div>

                    <div className="scroll">
                      <div className="messages">
                        {visibleMessages.map((message) => {
                          const role = message.role === 'assistant' || message.role === 'system' ? message.role : 'user';
                          const divkit = isDivkitMessage(message);
                          return (
                            <div key={message.id} className={`msg msg-${role}${divkit ? ' msg-wide' : ''}`}>
                              <div className="msg-meta">
                                <span className={`role role-${role}`}>{message.role}</span>
                                <span>·</span>
                                <span>{message.message_type || 'TextMessage'}</span>
                                <span>·</span>
                                <span>{message.timestamp.toLocaleTimeString()}</span>
                              </div>
                              <div className={`bubble${divkit ? ' bubble-divkit' : ''}`}>{renderMessageContent(message)}</div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>

                    <div className="composer">
                      <input
                        ref={inputRef}
                        id="composer-message"
                        type="text"
                        className="input input-main"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type a message, or send silence with an empty field"
                        disabled={loading || !sessionId}
                        autoFocus
                      />
                      <input
                        id="composer-lang"
                        type="text"
                        className="input input-lang"
                        value={messageLanguage}
                        onChange={(e) => setMessageLanguage(e.target.value)}
                        placeholder="lang"
                        title="Agent language for this message"
                        disabled={loading || !sessionId}
                      />
                      <button className="btn btn-primary" onClick={handleSend} disabled={loading || !sessionId}>
                        {inputMessage.trim() ? 'Send Message' : 'Send Silence'}
                      </button>
                    </div>

                    <details className="event-form">
                      <summary>Send user event</summary>
                      <div className="event-form-body">
                        <input
                          id="event-type"
                          type="text"
                          className="input input-type mono"
                          value={eventType}
                          onChange={(e) => setEventType(e.target.value)}
                          placeholder="event type, e.g. widget.add"
                          disabled={loading || !sessionId}
                        />
                        <textarea
                          id="event-payload"
                          rows={2}
                          className={`textarea mono${isEventPayloadValid() ? '' : ' is-invalid'}`}
                          value={eventPayloadText}
                          onChange={(e) => setEventPayloadText(e.target.value)}
                          placeholder='payload JSON (optional), e.g. {"product_id": "19445"}'
                          disabled={loading || !sessionId}
                        />
                        <button
                          className="btn btn-primary"
                          onClick={sendUserEvent}
                          disabled={loading || !sessionId || !eventType.trim() || !isEventPayloadValid()}
                        >
                          Send Event
                        </button>
                      </div>
                    </details>
                  </>
                ) : (
                  <>
                    <div className="panel-toolbar">
                      <span>Analytics events and raw model output</span>
                      <span className="count">{rawMessages.length}</span>
                    </div>
                    <div className="scroll">
                      {rawMessages.length === 0 ? (
                        <div className="empty">
                          <p className="empty-title">Nothing captured yet</p>
                          <p>Send a message and the agent iterations, analytics and memory snapshots land here.</p>
                        </div>
                      ) : (
                        <div className="log">
                          {rawMessages.map((message, index) => (
                            <div key={index} className="log-item">
                              <div className="log-head">
                                <span className={`role role-${message.role === 'assistant' || message.role === 'user' ? message.role : 'system'}`}>{message.role}</span>
                                <span className="chip">{message.event_name}</span>
                                {message.message_type && <span className="chip">{message.message_type}</span>}
                                {message.agent_language && <span className="chip">{message.agent_language}</span>}
                                {message.timestamp && <span className="time">{message.timestamp}</span>}
                              </div>
                              <div className="log-body">
                                <pre className="code">{message.content}</pre>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="panel">
              <div className="empty">
                <div className="empty-glyph" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M4 10h2M7 7v6M10 4v12M13 7v6M16 9v2" />
                  </svg>
                </div>
                <p className="empty-title">No active session</p>
                <p>Pick a session type on the left, load a scenario or point at an existing one, then create the session to start chatting.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default App;
